import { Checkbox, FormControlLabel, SelectChangeEvent } from '@mui/material';
import { ChangeEvent, FocusEvent } from 'react';
import { Controller } from 'react-hook-form';

import {
  asDate,
  getDateBoundsError,
} from 'portal/src/validators/dateValidator';

import FormAutocomplete from './FormAutocomplete';
import FormDatePicker from './FormDatePicker';
import {
  DATE_PICKER_MIN_YEAR,
  getDefaultDatePickerMaxDate,
} from './FormDatePicker.constants';
import FormDateRangePicker from './FormDateRangePicker';
import { patternInputType } from './FormFieldTypes';
import FormFileUploader from './FormFileUploader';
import FormNumericInput from './FormNumericInput';
import FormPatternInput from './FormPatternInput';
import FormSelectInput from './FormSelectInput';
import FormSwitchInput from './FormSwitchInput';
import FormTextInput from './FormTextInput';
import { VALIDATION_MSG } from './validationMessages';

const FormController = ({
  control,
  fieldConf,
  isViewMode = false,
}: {
  control: any;
  fieldConf: any;
  isViewMode?: boolean;
}) =>
  fieldConf.type === 'noField' ? (
    <div />
  ) : (
    <Controller
      name={fieldConf.name}
      control={control}
      rules={{
        validate: (value, context) => {
          const hasValue = Array.isArray(value)
            ? value.length > 0
            : // Allow `false` as valid for selectInput with boolean options
              value !== null && value !== undefined && value !== '';

          if (fieldConf.required && !hasValue) {
            return VALIDATION_MSG.required;
          }

          if (!fieldConf.required && !hasValue) {
            return true;
          }

          if (
            fieldConf.type === 'autocomplete' &&
            !fieldConf.allowFreeText &&
            !fieldConf.multiple
          ) {
            const isValid = fieldConf.options.some(
              ({ value: optionValue }) => optionValue === value,
            );
            if (!isValid) return VALIDATION_MSG.required;
          }

          if (fieldConf.type === 'datePicker' && value) {
            const date = asDate(value);

            if (!date) {
              return VALIDATION_MSG.invalidDate;
            }

            const dateError = getDateBoundsError(value, {
              minDate:
                fieldConf.minDate ?? new Date(DATE_PICKER_MIN_YEAR, 0, 1),
              maxDate: fieldConf.maxDate ?? getDefaultDatePickerMaxDate(),
            });

            if (dateError) {
              return dateError;
            }
          }

          if (fieldConf.validate) {
            return fieldConf.validate(value, context);
          }

          return true;
        },
      }}
      render={({ field, fieldState }) => {
        const { type = '', message } = fieldState.error ?? {};
        const helperText = message || type || fieldConf?.helperText;
        const hasError = fieldState.invalid;
        delete field.ref;

        switch (fieldConf.type) {
          case 'autocomplete':
            return (
              <FormAutocomplete
                clearIcon={fieldConf.clearIcon}
                clearOnBlur={fieldConf.clearOnBlur}
                disabled={fieldConf.disabled}
                disableClearable={fieldConf.disableClearable}
                field={field}
                freeSolo={fieldConf.freeSolo}
                error={hasError}
                helperText={helperText}
                infoTooltip={fieldConf.infoTooltip}
                label={fieldConf.label}
                multiple={fieldConf.multiple}
                options={fieldConf.options}
                onChange={(_, data) => {
                  const value = Array.isArray(data)
                    ? data.map((item) => item.value ?? item)
                    : (data?.value ?? data);

                  fieldConf.onChange?.(value);
                  field.onChange(value);
                }}
                placeholder={fieldConf.placeholder}
                required={fieldConf.required}
                readonly={isViewMode}
                size={fieldConf.size}
              />
            );
          case 'checkbox':
            return (
              <FormControlLabel
                label={fieldConf.label}
                labelPlacement={fieldConf.labelPlacement ?? 'end'}
                style={{ display: 'flex' }}
                control={
                  <Checkbox
                    checked={fieldConf.checked}
                    onChange={fieldConf.onChange}
                  />
                }
              />
            );
          case 'datePicker':
            return (
              <FormDatePicker
                {...field}
                disabled={fieldConf.disabled}
                label={fieldConf.label}
                placeholder={fieldConf.placeholder}
                size={fieldConf.size}
                readonly={isViewMode}
                required={fieldConf.required}
                error={hasError}
                minDate={fieldConf.minDate}
                maxDate={fieldConf.maxDate}
                other={fieldConf.other}
                helperText={helperText}
                slotProps={fieldConf.slotProps}
                onChange={(value) => {
                  fieldConf.onChange?.(value);
                  field.onChange(value);
                }}
                onBlur={(event: FocusEvent<HTMLInputElement>) => {
                  fieldConf.onBlur?.(event);
                  field.onBlur();
                }}
              />
            );
          case 'dateRangePicker':
            return (
              <FormDateRangePicker
                {...field}
                label={fieldConf.label}
                placeholder={fieldConf.placeholder}
                readonly={isViewMode}
                required={fieldConf.required}
                error={hasError}
                other={fieldConf.other}
                helperText={helperText}
                slotProps={fieldConf.slotProps}
                onChange={(value) => {
                  fieldConf.onChange?.(value);
                  field.onChange(value);
                }}
              />
            );
          case 'fileUploader':
            return (
              <FormFileUploader
                {...field}
                accept={fieldConf.accept}
                chooseDisabled={fieldConf.chooseDisabled}
                deleteDisabled={fieldConf.deleteDisabled}
                fileName={fieldConf.fileName}
                label={fieldConf.label}
                onChange={(file: File) => {
                  fieldConf.onChange?.(file);
                  field.onChange(file);
                }}
                onDelete={fieldConf.onDelete}
                viewPath={fieldConf.viewPath}
              />
            );
          case 'number':
            return (
              <FormNumericInput
                {...field}
                allowNegative={fieldConf.allowNegative}
                autoDecimalFormat={fieldConf.autoDecimalFormat}
                disabled={fieldConf.disabled}
                error={hasError}
                infoTooltip={fieldConf.infoTooltip}
                label={fieldConf.label}
                max={fieldConf.max}
                min={fieldConf.min}
                onChange={(value: number | null) => {
                  fieldConf.onChange?.(value);
                  field.onChange(value);
                }}
                prefix={fieldConf.adorment}
                suffix={fieldConf.suffix}
                placeholder={fieldConf.placeholder}
                readonly={isViewMode}
                required={fieldConf.required}
                thousandSeparator={fieldConf.thousandSeparator}
                helperText={helperText}
                helperAction={fieldConf.helperAction}
              />
            );
          case 'pattern':
            const {
              disabled,
              definitions,
              name,
              helperAction,
              label,
              maskPattern,
              placeholder,
              required,
              unmask,
            } = fieldConf as patternInputType;

            return (
              <FormPatternInput
                disabled={disabled}
                error={hasError}
                helperAction={helperAction}
                helperText={helperText}
                label={label}
                definitions={definitions}
                maskPattern={maskPattern}
                name={name}
                onValueChange={field.onChange}
                placeholder={placeholder}
                readOnly={isViewMode}
                required={required}
                unmask={unmask}
                value={field.value}
                size={fieldConf.size}
              />
            );
          case 'password':
            return (
              <FormTextInput
                {...field}
                disabled={fieldConf.disabled}
                placeholder={fieldConf.placeholder}
                label={fieldConf.label}
                error={hasError}
                onBlur={fieldConf.onBlur}
                readonly={isViewMode}
                required={fieldConf.required}
                type="password"
                inputProps={{
                  maxLength: fieldConf.maxLength,
                }}
                helperText={helperText}
              />
            );
          case 'selectInput':
            return (
              <FormSelectInput
                {...field}
                disabled={fieldConf.disabled}
                error={hasError}
                helperText={helperText}
                infoTooltip={fieldConf.infoTooltip}
                label={fieldConf.label}
                multiple={fieldConf.multiple}
                onChange={(e: SelectChangeEvent) => {
                  fieldConf.onChange?.(e.target.value);
                  field.onChange(e);
                }}
                readonly={isViewMode}
                required={fieldConf.required}
                size={fieldConf.size}
                values={fieldConf.values}
              />
            );
          case 'switch':
            return (
              <FormSwitchInput
                checked={!!field.value}
                disabled={fieldConf.disabled || isViewMode}
                field={field}
                infoTooltip={fieldConf.infoTooltip}
                label={fieldConf.label}
                name={fieldConf.name}
                required={fieldConf.required}
              />
            );
          default:
            return (
              <FormTextInput
                {...field}
                adornmentText={fieldConf.adornmentText}
                disabled={fieldConf.disabled}
                size={fieldConf.size}
                placeholder={fieldConf.placeholder}
                label={fieldConf.label}
                error={hasError}
                multiline={fieldConf.multiline}
                onBlur={fieldConf.onBlur}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  fieldConf.onChange?.(e);
                  field.onChange(e);
                }}
                readonly={isViewMode}
                required={fieldConf.required}
                type={fieldConf.inputType}
                inputProps={{
                  maxLength: fieldConf.maxLength,
                }}
                helperAction={fieldConf.helperAction}
                helperText={helperText}
              />
            );
        }
      }}
    />
  );

export default FormController;
